import React, { FormEvent, useEffect, useState } from "react";
import { Product } from "../types/products.types";

interface ProductsEditFormProps {
  product: Product;
  onSave: (updatedWine: Partial<Product>) => Promise<void>;
  onCancel: () => void;
}

const ProductEditingForm: React.FC<ProductsEditFormProps> = ({
  product,
  onSave,
  onCancel,
}) => {
  const [name, setName] = useState(product.name);
  const [category1, setCategory1] = useState(product.category_1 || null);
  const [category2, setCategory2] = useState(product.category_2 || null);
  const [category3, setCategory3] = useState(product.category_3 || null);
  const [llmDescription, setLlmDescription] = useState(
    product.description || ""
  );
  const [llmPairings, setLlmPairings] = useState(product.pairings || "");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(product.name);
    setCategory1(product.category_1 || null);
    setCategory2(product.category_2 || null);
    setCategory3(product.category_3 || null);
    setLlmDescription(product.description || "");
    setLlmPairings(product.pairings || "");
  }, [product]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSaving(true);
    setError(null);

    const updateProduct: Partial<Product> = {
      id: product.id, // Always include product_id for identification
      name: name, // Assuming name can be edited too
      category_1: category1,
      category_2: category2,
      category_3: category3,
      description: llmDescription,
      pairings: llmPairings,
    };

    try {
      await onSave(updateProduct);
    } catch (err: any) {
      setError(err.message || "Failed to save product.");
    } finally {
      setIsSaving(false);
    }
  };

  const formFieldStyle: React.CSSProperties = {
    marginBottom: "15px",
  };
  const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: "5px",
    fontWeight: "bold",
  };
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    boxSizing: "border-box",
  };
  const textAreaStyle: React.CSSProperties = {
    ...inputStyle,
    minHeight: "100px",
    resize: "vertical",
  };
  const textAreaStyleCategory: React.CSSProperties = {
    width: "100px",
    padding: "8px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    boxSizing: "border-box",
    minHeight: "50px",
    resize: "vertical",
  };
  const buttonContainerStyle: React.CSSProperties = {
    marginTop: "20px",
  };

  return (
    <div
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        backgroundColor: "white",
        padding: "30px",
        borderRadius: "8px",
        boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
        zIndex: 1000,
        width: "90%",
        maxWidth: "600px",
        maxHeight: "90vh",
        overflowY: "auto",
      }}
    >
      <h3>
        Modifica Vino: {product.name} (ID: {product.id})
      </h3>
      <form onSubmit={handleSubmit}>
        <div style={formFieldStyle}>
          <label htmlFor="name" style={labelStyle}>
            Nome:
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
          />
        </div>
        {/* Add Category inputs if they should be editable */}
        {/* For now, focusing on description and pairings */}
        <div style={formFieldStyle}>
          <label htmlFor="llmDescription" style={labelStyle}>
            Descrizione (LLM):
          </label>
          <textarea
            id="llmDescription"
            value={llmDescription}
            onChange={(e) => setLlmDescription(e.target.value)}
            style={textAreaStyle}
          />
        </div>
        <div style={formFieldStyle}>
          <label htmlFor="llmPairings" style={labelStyle}>
            Abbinamenti (LLM):
          </label>
          <textarea
            id="llmPairings"
            value={llmPairings}
            onChange={(e) => setLlmPairings(e.target.value)}
            style={textAreaStyle}
          />
        </div>
        {/* CATEGORIE */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "5px",
            marginBottom: "15px",
          }}
        >
          <div style={formFieldStyle}>
            <label htmlFor="category1_Name" style={labelStyle}>
              NAME Categoria 1:
            </label>
            <textarea
              id="category1_Name"
              value={`${category1?.name || "N/A"}`}
              onChange={(e) =>
                setCategory1({ name: e.target.value, id: category1?.id || 0 })
              }
              style={textAreaStyleCategory}
            />
          </div>
          <div style={formFieldStyle}>
            <label htmlFor="category1" style={labelStyle}>
              ID Categoria 1:
            </label>
            <textarea
              id="category1"
              value={`${category1?.id || "N/A"}`}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setCategory1({
                  id: value ? Number(value) : 0,
                  name: category1?.name || "",
                });
              }}
              style={textAreaStyleCategory}
            />
          </div>

          <div style={formFieldStyle}>
            <label htmlFor="category2_Name" style={labelStyle}>
              Name Categoria 2:
            </label>
            <textarea
              id="category2_Name"
              value={`${category2?.name || "N/A"}`}
              onChange={(e) =>
                setCategory2({ name: e.target.value, id: category2?.id || 0 })
              }
              style={textAreaStyleCategory}
            />
          </div>
          <div style={formFieldStyle}>
            <label htmlFor="category2_ID" style={labelStyle}>
              ID Categoria 2:
            </label>
            <textarea
              id="category2_ID"
              value={`${category2?.id || "N/A"}`}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setCategory2({
                  id: value ? Number(value) : 0,
                  name: category2?.name || "",
                });
              }}
              style={textAreaStyleCategory}
            />
          </div>

          <div style={formFieldStyle}>
            <label htmlFor="category3_Name" style={labelStyle}>
              Name Categoria 3:
            </label>
            <textarea
              id="category3_Name"
              value={`${category3?.name || "N/A"}`}
              onChange={(e) =>
                setCategory3({ name: e.target.value, id: category3?.id || 0 })
              }
              style={textAreaStyleCategory}
            />
          </div>
          <div style={formFieldStyle}>
            <label htmlFor="category3_ID" style={labelStyle}>
              ID Categoria 3:
            </label>
            <textarea
              id="category3_ID"
              value={`${category3?.id || "N/A"}`}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setCategory3({
                  id: value ? Number(value) : 0,
                  name: category3?.name || "",
                });
              }}
              style={textAreaStyleCategory}
            />
          </div>
        </div>
        {error && <p style={{ color: "red" }}>{error}</p>}

        <div style={buttonContainerStyle}>
          <button
            type="submit"
            disabled={isSaving}
            style={{ marginRight: "10px", padding: "10px 15px" }}
          >
            {isSaving ? "Salvataggio..." : "Salva Modifiche"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            style={{ padding: "10px 15px" }}
          >
            Annulla
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductEditingForm;
